// Desenul unui TIP de proiect: aceeași clădire, întreagă sau doar scheletul ei.
//
// Proiectul complet pornește de la teren și primește clădirea întreagă, deci
// desenul lui e clădirea pe lotul ei: parcela punctată ca pe actele din dosar,
// fațadele cu ferestre și balcoane, rampa spre subsol, copacii. Proiectul de
// structură pornește de la o arhitectură gata făcută și primește numai
// structura, deci desenul lui e SCHELETUL aceleiași clădiri: stâlpi, grinzi,
// pereți de beton, planșee, radier — în convenția programelor de calcul din
// `paletaDesen.ts`, cu anvelopa arhitecturii doar ca urmă punctată.
//
// Aceeași clădire în amândouă, din același unghi: diferența dintre desene e
// chiar diferența dintre ce primește fiecare proiect.
//
// Clădirea: bloc 30 × 14 m, P+4 (5 niveluri × 3 m), pe un lot 44 × 26 m.
// Proiecția e izometrică, la 30°, ca miniaturile din lista de proiecte
// (`Volum.tsx`): X spre dreapta-jos, Y spre stânga-jos, deci fețele văzute
// sunt Y = 14 (fațada lungă, în lumină) și X = 30 (capătul, în umbră).

import type { ReactElement } from "react";
import type { TipProiect } from "../api";
import { PAL, alfa } from "../structure/paletaDesen";

const C = Math.cos(Math.PI / 6);
const S = 0.5;

type P3 = readonly [number, number, number];
type Iso = { s: number; ox: number; oy: number };

// Machetă albă, ca miniaturile din listă: fața în lumină albă, capătul în
// umbră gri deschis, conturul gri. Accentul temei stă doar pe conturul lotului.
const LUMINA = "#ffffff";
const UMBRA = "#e0e4e8";
const CAPAC = "#f3f5f7";
const CONTUR = "rgba(84,92,104,.58)";
const NIVEL = "rgba(84,92,104,.2)";
const GEAM = "#d2e2e9";
const GEAM_UMBRA = "#c1d1d9";
const LOT = "#e6eedf";
const COROANA = "#cfe2c3";
const COROANA_CONTUR = "#9fbf8e";
const COROANA_LUMINA = "#e1edd8";
const TRUNCHI = "#9c8f7a";

const pe = (iso: Iso, [X, Y, Z]: P3): [number, number] =>
  [iso.ox + (X - Y) * C * iso.s, iso.oy + (X + Y) * S * iso.s - Z * iso.s];

/** Grosimea unei linii, proporțională cu scara: 6 px pe metru e baza. */
const gros = (iso: Iso, baza: number) => Math.max(0.45, +(baza * iso.s / 6).toFixed(2));

function potriveste(puncte: readonly P3[], w: number, h: number,
                    pad: readonly [number, number]): Iso {
  const xs = puncte.map(([X, Y]) => (X - Y) * C);
  const ys = puncte.map(([X, Y, Z]) => (X + Y) * S - Z);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const s = Math.min((w - 2 * pad[0]) / (x1 - x0), (h - 2 * pad[1]) / (y1 - y0));
  return { s, ox: (w - s * (x1 + x0)) / 2, oy: (h - s * (y1 + y0)) / 2 };
}

/** Desenul se scrie ca o listă de elemente; fiecare își ia cheia din poziție. */
class Hartie {
  readonly el: ReactElement[] = [];

  constructor(readonly iso: Iso) {}

  private d(pts: readonly P3[], inchis: boolean) {
    const d = pts.map((q) => pe(this.iso, q).map((v) => v.toFixed(1)).join(" ")).join("L");
    return `M${d}${inchis ? "Z" : ""}`;
  }

  poli(pts: readonly P3[], fill: string, stroke = "none", sw = 1,
       extra: { dash?: string; opacity?: number } = {}) {
    this.el.push(
      <path key={this.el.length} d={this.d(pts, true)} fill={fill} stroke={stroke}
            strokeWidth={stroke === "none" ? undefined : gros(this.iso, sw)}
            strokeLinejoin="round" strokeDasharray={extra.dash} opacity={extra.opacity} />);
  }

  linie(pts: readonly P3[], stroke: string, sw = 1,
        extra: { dash?: string; opacity?: number; cap?: "butt" | "round" } = {}) {
    this.el.push(
      <path key={this.el.length} d={this.d(pts, false)} fill="none" stroke={stroke}
            strokeWidth={gros(this.iso, sw)} strokeLinecap={extra.cap ?? "round"}
            strokeLinejoin="round" strokeDasharray={extra.dash} opacity={extra.opacity} />);
  }

  /** Fețele văzute ale unei cutii: stânga (Y = y1), dreapta (X = x1), sus. */
  cutie([x0, x1, y0, y1, z0, z1]: readonly number[],
        [sus, stanga, dreapta]: readonly string[], contur: string, sw = 0.9) {
    this.poli([[x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1]], stanga, contur, sw);
    this.poli([[x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1]], dreapta, contur, sw);
    this.poli([[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], sus, contur, sw);
  }

  copac(X: number, Y: number, r = 1.7) {
    const { s } = this.iso;
    const [ux, uy] = pe(this.iso, [X + 0.9, Y + 0.7, 0]);
    this.el.push(<ellipse key={this.el.length} cx={ux} cy={uy} rx={r * s * 1.22} ry={r * s * 0.7}
                          fill="rgba(40,70,60,.14)" />);
    this.linie([[X, Y, 0], [X, Y, 1.3]], TRUNCHI, 1.3);
    const [kx, ky] = pe(this.iso, [X, Y, 2.7]);
    this.el.push(<circle key={this.el.length} cx={kx} cy={ky} r={r * s} fill={COROANA}
                         stroke={COROANA_CONTUR} strokeWidth={gros(this.iso, 0.9)} />);
    this.el.push(<circle key={this.el.length} cx={kx - r * s * 0.32} cy={ky - r * s * 0.3}
                         r={r * s * 0.42} fill={COROANA_LUMINA} />);
  }
}

// --- proiectul complet: clădirea întreagă, pe lotul ei ----------------------

const COLTURI_LOT: readonly P3[] = [[-7, -6, 0], [37, -6, 0], [37, 20, 0], [-7, 20, 0]];
const COPACI_SPATE: readonly [number, number][] = [[-3.8, 3.2], [-4.3, 10.4]];
const COPACI_FATA: readonly [number, number][] = [[-2.6, 16.8], [6.2, 17.6], [24.8, 17.6], [33.6, 16.4]];
const CADRU_COMPLET: readonly P3[] = [
  ...COLTURI_LOT, [0, 0, 17.6], [30, 14, 15], [0, 14, 15], [30, 0, 15], [12, 4.5, 17.6],
];

function scenaCompleta(h: Hartie, detaliu: boolean) {
  const acc = "var(--acc, #0b7f92)";
  h.poli(COLTURI_LOT, LOT);
  if (detaliu) {
    // subsolul trece de amprenta clădirii: punctat, gri
    h.poli([[-3, -3, 0], [34, -3, 0], [34, 17, 0], [-3, 17, 0]], "none", "#9aa2ad", 0.8,
           { dash: "2 3", opacity: 0.8 });
  }
  h.poli(COLTURI_LOT, "none", acc, 1.3, { dash: "5 4" });
  for (const q of COLTURI_LOT) {
    const [x, y] = pe(h.iso, q);
    h.el.push(<circle key={h.el.length} cx={x} cy={y} r={Math.max(1.4, 2.5 * h.iso.s / 6)}
                      fill="#ffffff" stroke={acc} strokeWidth={gros(h.iso, 1.2)} />);
  }
  // rampa spre subsol și aleea de la intrare
  h.poli([[31, 2.6, 0], [35, 2.6, 0], [35, 12.4, 0], [31, 12.4, 0]], "#d8dde2",
         "rgba(84,92,104,.35)", 0.7);
  h.poli([[31, 2.6, 0], [35, 2.6, 0], [35, 4.1, 0], [31, 4.1, 0]], "#8f99a4");
  if (detaliu) {
    for (const y of [5.6, 7.6, 9.6, 11.4]) {
      h.linie([[31.8, y + 0.8, 0], [33, y, 0], [34.2, y + 0.8, 0]], "#a6afb9", 0.8);
    }
  }
  h.poli([[13.9, 14, 0], [16.1, 14, 0], [16.1, 20, 0], [13.9, 20, 0]], "#f4f5f6",
         "rgba(84,92,104,.25)", 0.6);
  // umbra purtată, spre dreapta-față
  h.poli([[0, 14, 0], [30, 14, 0], [30, 0, 0], [35.5, 3.2, 0], [35.5, 17.2, 0], [5.5, 17.2, 0]],
         "rgba(40,60,70,.13)");
  if (detaliu) COPACI_SPATE.forEach(([X, Y]) => h.copac(X, Y));

  h.cutie([0, 30, 0, 14, 0, 15], [CAPAC, LUMINA, UMBRA], CONTUR);
  if (detaliu) {
    for (const z of [3, 6, 9, 12]) {
      h.linie([[0, 14, z], [30, 14, z]], NIVEL, 0.7);
      h.linie([[30, 0, z], [30, 14, z]], NIVEL, 0.7);
    }
    // ferestrele: zece pe fațada lungă, patru pe capăt, pe fiecare nivel;
    // la parter, în locul a două dintre ele, intrarea
    for (let k = 0; k < 5; k += 1) {
      const z0 = 3 * k + 1.0;
      const z1 = z0 + 1.4;
      for (let i = 0; i < 10; i += 1) {
        if (k === 0 && (i === 4 || i === 5)) continue;
        const c = 1.5 + 3 * i;
        h.poli([[c - 0.8, 14, z0], [c + 0.8, 14, z0], [c + 0.8, 14, z1], [c - 0.8, 14, z1]], GEAM);
      }
      for (const c of [2.2, 5.6, 8.6, 11.9]) {
        h.poli([[30, c - 0.8, z0], [30, c + 0.8, z0], [30, c + 0.8, z1], [30, c - 0.8, z1]], GEAM_UMBRA);
      }
    }
    h.poli([[13.7, 14, 0], [16.3, 14, 0], [16.3, 14, 2.5], [13.7, 14, 2.5]], "#b8cfd9",
           "rgba(84,92,104,.4)", 0.6);
    h.cutie([13.1, 16.9, 14, 15.5, 2.62, 2.86], ["#f7f8f9", "#e7eaed", "#d6dbe0"],
            "rgba(84,92,104,.45)", 0.6);
    // balcoanele, de la etajul 1 în sus
    for (let k = 1; k < 5; k += 1) {
      const z = 3 * k;
      for (const [x0, x1] of [[3, 8], [22, 27]]) {
        h.cutie([x0, x1, 14, 15.3, z - 0.22, z], ["#f7f8f9", "#e7eaed", "#d6dbe0"],
                "rgba(84,92,104,.45)", 0.6);
        h.poli([[x1, 14, z], [x1, 15.3, z], [x1, 15.3, z + 1], [x1, 14, z + 1]],
               "rgba(180,200,210,.45)", "rgba(84,92,104,.35)", 0.5);
        h.poli([[x0, 15.3, z], [x1, 15.3, z], [x1, 15.3, z + 1], [x0, 15.3, z + 1]],
               "rgba(210,228,235,.55)", "rgba(84,92,104,.4)", 0.6);
      }
    }
    h.poli([[0.4, 0.4, 15], [29.6, 0.4, 15], [29.6, 13.6, 15], [0.4, 13.6, 15]], "none",
           "rgba(84,92,104,.28)", 0.6);
  }
  // casa scării iese peste acoperiș
  h.cutie([12, 17, 4.5, 9.5, 15, 17.6], [CAPAC, LUMINA, UMBRA], CONTUR, 0.8);
  if (detaliu) COPACI_FATA.forEach(([X, Y]) => h.copac(X, Y));
}

// --- proiectul de structură: scheletul aceleiași clădiri ---------------------

const AXE_X = [0, 6, 12, 18, 24, 30];
const AXE_Y = [0, 7, 14];
const NIVELURI = [0, 3, 6, 9, 12, 15];
const MIEZ = [12, 17, 4.5, 9.5] as const;
const SOL: readonly P3[] = [[-6, -5, 0], [36, -5, 0], [36, 19, 0], [-6, 19, 0]];
const CADRU_STRUCTURA: readonly P3[] = [
  ...SOL, [30.8, 14.8, -0.9], [12, 4.5, 17.6], [0, 0, 15.3],
];

function scenaStructura(h: Hartie, detaliu: boolean) {
  const placa = (z: number, [x0, x1, y0, y1] = [0, 30, 0, 14]) =>
    h.cutie([x0, x1, y0, y1, z - 0.3, z],
            [alfa(PAL.placa, 0.2), alfa(PAL.placa, 0.55), alfa(PAL.placa, 0.45)],
            alfa(PAL.placa, 0.95), 0.8);
  const perete = ([xa, ya]: readonly number[], [xb, yb]: readonly number[], z0: number, z1: number) =>
    h.poli([[xa, ya, z0], [xb, yb, z0], [xb, yb, z1], [xa, ya, z1]],
           alfa(PAL.pereteBeton, 0.2), PAL.pereteBeton, 0.9);

  // terenul, ca în vizualizatorul 3D: fundal rece și grila lui
  h.poli(SOL, PAL.fundal, PAL.grilaMare, 0.7);
  if (detaliu) {
    for (let X = -3; X < 36; X += 3) h.linie([[X, -5, 0], [X, 19, 0]], PAL.grilaMica, 0.6);
    for (let Y = -2; Y < 19; Y += 3) h.linie([[-6, Y, 0], [36, Y, 0]], PAL.grilaMica, 0.6);
  }
  h.cutie([-0.8, 30.8, -0.8, 14.8, -0.9, 0],
          [alfa(PAL.fundatie, 0.4), alfa(PAL.fundatie, 0.75), alfa(PAL.fundatie, 0.9)],
          PAL.fundatie, 0.8);

  const [mx0, mx1, my0, my1] = MIEZ;
  // pereții din spate înaintea stâlpilor, cei din față după ei
  const spate = [[[0, 3.5], [0, 10.5]], [[mx0, my0], [mx1, my0]], [[mx0, my0], [mx0, my1]]];
  const fata = [[[30, 3.5], [30, 10.5]], [[mx0, my1], [mx1, my1]], [[mx1, my0], [mx1, my1]]];
  // la capete, pereții țin locul stâlpilor din axul din mijloc
  const stalpi = AXE_X.flatMap((X) => AXE_Y.map((Y) => [X, Y] as const))
    .filter(([X, Y]) => !(Y === 7 && (X === 0 || X === 30)))
    .sort((a, b) => a[0] + a[1] - (b[0] + b[1]));
  for (let k = 0; k < 5; k += 1) {
    const zb = NIVELURI[k];
    const zt = NIVELURI[k + 1];
    if (k) placa(zb);
    spate.forEach(([a, b]) => perete(a, b, zb, zt));
    for (const [X, Y] of stalpi) h.linie([[X, Y, zb], [X, Y, zt - 0.3]], PAL.stalp, 2.3, { cap: "butt" });
    if (detaliu) {
      for (const Y of AXE_Y) {
        for (let i = 0; i + 1 < AXE_X.length; i += 1) {
          h.linie([[AXE_X[i], Y, zt - 0.35], [AXE_X[i + 1], Y, zt - 0.35]], PAL.grinda, 1.3);
        }
      }
      for (const X of AXE_X) h.linie([[X, 0, zt - 0.35], [X, 14, zt - 0.35]], PAL.grinda, 1.3);
    }
    fata.forEach(([a, b]) => perete(a, b, zb, zt));
  }
  placa(15);
  // miezul continuă peste acoperiș, cu placa lui
  [...spate.slice(1), ...fata.slice(1)].forEach(([a, b]) => perete(a, b, 15, 17.3));
  placa(17.6, [mx0, mx1, my0, my1]);
  if (detaliu) {
    // anvelopa arhitecturii, ca urmă: structura stă în ea
    const e = 0.45;
    const urma = { dash: "3 3", opacity: 0.75 };
    const par = "var(--hair, #9aa2ad)";
    h.poli([[-e, -e, 15.4], [30 + e, -e, 15.4], [30 + e, 14 + e, 15.4], [-e, 14 + e, 15.4]],
           "none", par, 0.8, urma);
    for (const [X, Y] of [[-e, 14 + e], [30 + e, 14 + e], [30 + e, -e]]) {
      h.linie([[X, Y, 0], [X, Y, 15.4]], par, 0.8, urma);
    }
    h.linie([[-e, 14 + e, 0], [30 + e, 14 + e, 0], [30 + e, -e, 0]], par, 0.8, urma);
  }
}

/** Elementele desenului, potrivite într-o cutie `w × h`, fără `<svg>` în jur:
 *  miniatura din lista de proiecte le pune peste fundalul ei. */
export function desenTip(tip: TipProiect, w: number, h: number,
                         pad: readonly [number, number] = [14, 12], detaliu = true): ReactElement[] {
  const cadru = tip === "complet" ? CADRU_COMPLET : CADRU_STRUCTURA;
  const hartie = new Hartie(potriveste(cadru, w, h, pad));
  if (tip === "complet") scenaCompleta(hartie, detaliu);
  else scenaStructura(hartie, detaliu);
  return hartie.el;
}

type Props = {
  tip: TipProiect;
  w: number;
  h: number;
  pad?: readonly [number, number];
  /** Fără ferestre, copaci și grinzi: pentru desenele mici, unde detaliul ar fi zgomot. */
  detaliu?: boolean;
  /** Textul pentru cititoarele de ecran; fără el, desenul e doar decor. */
  titlu?: string;
};

export default function IlustratieProiect({ tip, w, h, pad, detaliu = true, titlu }: Props) {
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="ilustratie-proiect"
         role={titlu ? "img" : undefined} aria-label={titlu} aria-hidden={titlu ? undefined : true}>
      {titlu && <title>{titlu}</title>}
      {desenTip(tip, w, h, pad, detaliu)}
    </svg>
  );
}
